/**
 * AnalyticsOverviewTab - Tab content for overall analytics overview
 */
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Progress } from '../ui/progress';
import { 
  TrendingUp,
  DollarSign,
  CreditCard,
  BarChart3,
  Loader2
} from 'lucide-react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { CATEGORY_COLORS, getCategoryIcon } from './constants';

const AnalyticsOverviewTab = ({ overviewAnalytics, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!overviewAnalytics) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <BarChart3 className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">No analytics data yet</p>
          <p className="text-sm text-muted-foreground">Create cards and make transactions to see analytics</p>
        </CardContent>
      </Card>
    );
  }

  const categoryData = overviewAnalytics?.spending_by_category 
    ? Object.entries(overviewAnalytics.spending_by_category).map(([name, value]) => ({
        name,
        value,
        color: CATEGORY_COLORS[name] || '#64748b'
      }))
    : [];

  const totalSpent = overviewAnalytics?.total_spent_all_cards || 0;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Spent (All Cards)</p>
                <p className="text-2xl font-heading font-bold">
                  ${totalSpent.toFixed(2)}
                </p>
              </div>
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Total Transactions</p>
            <p className="text-2xl font-heading font-bold">
              {overviewAnalytics?.total_transactions_all_cards || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Active Cards</p>
            <p className="text-2xl font-heading font-bold">
              {overviewAnalytics?.card_count || 0}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground">Avg. per Transaction</p>
            <p className="text-2xl font-heading font-bold">
              ${overviewAnalytics?.average_transaction_all_cards?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Spending Trend */}
        {overviewAnalytics?.spending_trend && overviewAnalytics.spending_trend.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                30-Day Spending Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64 w-full min-w-0">
                <ResponsiveContainer width="100%" height={256} minWidth={200}>
                  <AreaChart data={overviewAnalytics.spending_trend}>
                    <defs>
                      <linearGradient id="colorOverallSpending" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => value.slice(5)}
                    />
                    <YAxis 
                      tick={{ fontSize: 11 }}
                      tickFormatter={(value) => `$${value}`}
                    />
                    <Tooltip 
                      formatter={(value) => [`$${value.toFixed(2)}`, 'Spent']}
                      contentStyle={{ 
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px'
                      }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="amount" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      fill="url(#colorOverallSpending)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Category Breakdown */}
        {categoryData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-purple-500" />
                Spending by Category
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-6">
                <div className="w-48 h-48 min-w-[192px]">
                  <ResponsiveContainer width={192} height={192}>
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        dataKey="value"
                        labelLine={false}
                      >
                        {categoryData.map((entry) => (
                          <Cell key={`cell-${entry.name}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => [`$${value.toFixed(2)}`, '']}
                        contentStyle={{ 
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px'
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-3">
                  {categoryData.map((category) => {
                    const Icon = getCategoryIcon(category.name);
                    const percentage = totalSpent > 0 ? (category.value / totalSpent * 100).toFixed(1) : 0;
                    return (
                      <div key={`cat-${category.name}`} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <div 
                              className="w-3 h-3 rounded-full" 
                              style={{ backgroundColor: category.color }}
                            />
                            <Icon className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">{category.name}</span>
                          </div>
                          <span className="text-sm font-medium">${category.value.toFixed(2)}</span>
                        </div>
                        <Progress 
                          value={parseFloat(percentage)} 
                          className="h-1.5"
                          style={{ '--progress-color': category.color }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Per Card Breakdown */}
      {overviewAnalytics?.cards_summary && overviewAnalytics.cards_summary.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" />
              Per Card Summary
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {overviewAnalytics.cards_summary.map((cardData) => {
                const limitUsedPercent = cardData.spending_limit > 0 
                  ? (cardData.total_spent / cardData.spending_limit) * 100 
                  : 0;
                return (
                  <div 
                    key={cardData.card_id}
                    className="p-4 rounded-xl border hover:border-emerald-500/50 transition-colors"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5 text-muted-foreground" />
                        <span className="font-mono text-sm">•••• {cardData.last_four}</span>
                      </div>
                      <span className="text-xs text-muted-foreground capitalize">
                        {cardData.card_type}
                      </span>
                    </div>
                    <p className="text-2xl font-bold mb-1">
                      ${cardData.total_spent.toFixed(2)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {cardData.transaction_count} transactions
                    </p>
                    <Progress 
                      value={Math.min(limitUsedPercent, 100)} 
                      className="mt-3 h-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      {limitUsedPercent.toFixed(1)}% of ${cardData.spending_limit.toLocaleString()} limit
                    </p>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AnalyticsOverviewTab;
